// The prompt tab (spec 14 §4.3, D-512 ruling 6, D-536 ruling 7): the active
// prompt read-only, the version history by source, and Override, which saves
// a `tenant_override` version behind the typed confirmation. Saving and
// activating are both `agents.config.manage`'s.

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { StatusChip } from "../../../../components/status-chip";
import { CardSkeleton, EmptyState, ErrorState } from "../../../../components/states";
import { toast } from "../../../../components/toast";
import { formatTimestamp } from "../../../../lib/datetime";
import { requestIdOf } from "../../../../lib/request";
import {
  activatePromptVersion,
  agentsKeys,
  createPromptVersion,
  usePromptVersions,
} from "../hooks/agents-queries";
import {
  OVERRIDE_CONFIRMATION,
  type AgentDetail,
  type AgentPromptVersion,
} from "../lib/agents-lib";
import { errorText } from "../lib/labels";

export function PromptTab({ agent, canConfig }: { agent: AgentDetail; canConfig: boolean }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const versions = usePromptVersions(agent.id);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [changelog, setChangelog] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: agentsKeys.promptVersions(agent.id) });
    void queryClient.invalidateQueries({ queryKey: agentsKeys.detail(agent.id) });
  };

  if (versions.isPending)
    return <CardSkeleton lines={6} label={t("agents.detail.prompt.loading")} />;
  if (versions.isError) {
    return (
      <ErrorState
        title={t("agents.detail.prompt.loadError")}
        retryLabel={t("agents.common.retry")}
        onRetry={() => {
          void versions.refetch();
        }}
        requestId={requestIdOf(versions.error)}
        requestIdLabel={t("agents.common.requestId")}
      />
    );
  }

  const active = versions.data.find((v) => v.is_active);

  const activate = (version: AgentPromptVersion) => {
    setBusy(true);
    void activatePromptVersion(agent.id, version.id)
      .then(() => {
        toast.success(t("agents.detail.prompt.activated", { version: version.version }));
      })
      .catch((error: unknown) => {
        toast.error(errorText(t, error));
      })
      .finally(() => {
        setBusy(false);
        refresh();
      });
  };

  const columns: DataTableColumn<AgentPromptVersion>[] = [
    {
      key: "version",
      header: t("agents.detail.prompt.columns.version"),
      kind: "id",
      phonePriority: 1,
      render: (v) => t("agents.detail.prompt.version", { version: v.version }),
    },
    {
      key: "source",
      header: t("agents.detail.prompt.columns.source"),
      phonePriority: 2,
      render: (v) => t(`agents.detail.prompt.source.${v.source}`),
    },
    {
      key: "changelog",
      header: t("agents.detail.prompt.columns.changelog"),
      render: (v) => v.changelog,
    },
    {
      key: "created",
      header: t("agents.detail.prompt.columns.created"),
      kind: "date",
      render: (v) => formatTimestamp(v.created_at, i18n.language),
    },
    {
      key: "state",
      header: t("agents.detail.prompt.columns.state"),
      actions: true,
      phonePriority: 3,
      render: (v) =>
        v.is_active ? (
          <StatusChip variant="ok" data-testid="prompt-active-chip">
            {t("agents.detail.prompt.isActive")}
          </StatusChip>
        ) : canConfig ? (
          <Button
            disabled={busy}
            onClick={() => {
              activate(v);
            }}
            data-testid="prompt-activate"
          >
            {t("agents.detail.prompt.activate")}
          </Button>
        ) : null,
    },
  ];

  const canSave =
    text.trim().length > 0 &&
    changelog.trim().length > 0 &&
    confirm === OVERRIDE_CONFIRMATION &&
    !busy;

  return (
    <div className="ag-stack" data-testid="prompt-tab">
      <Card
        title={t("agents.detail.prompt.active")}
        action={
          canConfig ? (
            <Button
              onClick={() => {
                setText(active?.system_prompt_md ?? "");
                setChangelog("");
                setConfirm("");
                setEditing(true);
              }}
              data-testid="prompt-override"
            >
              {t("agents.detail.prompt.override")}
            </Button>
          ) : undefined
        }
      >
        <Card.Body>
          {active === undefined ? (
            <p className="ag-hint">{t("agents.detail.prompt.empty")}</p>
          ) : (
            <pre className="ag-prompt" data-testid="prompt-active-text">
              {active.system_prompt_md}
            </pre>
          )}
        </Card.Body>
      </Card>
      <Card title={t("agents.detail.prompt.history")} count={versions.data.length}>
        <DataTable
          columns={columns}
          rows={versions.data}
          rowKey={(v) => v.id}
          rowTestId="prompt-version-row"
          empty={<EmptyState title={t("agents.detail.prompt.empty")} body="" />}
          clientPager={{
            range: (from, to, total) => t("agents.common.pageOf", { from, to, total }),
            prevLabel: t("agents.common.previous"),
            nextLabel: t("agents.common.next"),
          }}
          data-testid="prompt-versions"
        />
      </Card>
      {editing ? (
        <Modal
          label={t("agents.detail.prompt.overrideTitle")}
          heading={t("agents.detail.prompt.overrideTitle")}
          closeLabel={t("agents.common.close")}
          onClose={() => {
            setEditing(false);
          }}
          pending={busy}
          testId="prompt-override-dialog"
        >
          <div className="ag-stack">
            <p className="ag-hint">{t("agents.detail.prompt.overrideBody")}</p>
            <Field label={t("agents.detail.prompt.promptText")} wide>
              <textarea
                className="ds-input ag-textarea"
                maxLength={50_000}
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                }}
                data-testid="prompt-override-text"
              />
            </Field>
            <Field label={t("agents.detail.prompt.changelog")} wide>
              <input
                className="ds-input"
                maxLength={2_000}
                value={changelog}
                onChange={(e) => {
                  setChangelog(e.target.value);
                }}
                data-testid="prompt-override-changelog"
              />
            </Field>
            <Field label={t("agents.detail.prompt.confirmLabel", { word: OVERRIDE_CONFIRMATION })}>
              <input
                className="ds-input"
                value={confirm}
                autoComplete="off"
                onChange={(e) => {
                  setConfirm(e.target.value);
                }}
                data-testid="prompt-override-confirm"
              />
            </Field>
            <div className="ag-decide">
              <Button
                onClick={() => {
                  setEditing(false);
                }}
              >
                {t("agents.common.cancel")}
              </Button>
              <Button
                variant="primary"
                disabled={!canSave}
                onClick={() => {
                  setBusy(true);
                  void createPromptVersion(agent.id, {
                    system_prompt_md: text,
                    changelog: changelog.trim(),
                  })
                    .then(() => {
                      toast.success(t("agents.detail.prompt.created"));
                      setEditing(false);
                    })
                    .catch((error: unknown) => {
                      toast.error(errorText(t, error));
                    })
                    .finally(() => {
                      setBusy(false);
                      refresh();
                    });
                }}
                data-testid="prompt-override-save"
              >
                {t("agents.detail.prompt.create")}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
